import { createModalFocusScope } from '../../components/modal-focus-scope.js';
import { generateRoute } from '../../routing/router.js';
import { createReorderWriteQueue } from './reorder-write-queue.js';
import { createSectionMap } from './section-map.js';

/**
 * Section order lives in `game_statuses.position`, so it follows the account across devices and is
 * what the export carries. The modal writes straight to the server; there is no client-side copy of
 * the order to drift out of sync.
 */
export function createDashboardOrderController({ sectionsContainer, sectionMap }) {
    function applySectionOrder(order) {
        const fragment = document.createDocumentFragment();

        order.forEach((statusValue) => {
            const section = sectionMap.get(statusValue);
            if (!(section instanceof HTMLElement)) {
                return;
            }

            fragment.append(section);
        });

        sectionsContainer.append(fragment);
    }

    function initializeModal() {
        const modal = document.querySelector('[data-dashboard-order-modal]');
        if (!(modal instanceof HTMLElement)) {
            return;
        }

        const openButton = document.querySelector('[data-dashboard-order-open]');
        if (!(openButton instanceof HTMLElement)) {
            return;
        }

        const orderList = modal.querySelector('[data-dashboard-order-list]');
        const statusMessage = modal.querySelector('[data-dashboard-order-status]');
        const dialog = modal.querySelector('[data-dashboard-order-dialog]');

        if (
            !(orderList instanceof HTMLElement)
            || !(statusMessage instanceof HTMLElement)
            || !(dialog instanceof HTMLElement)
        ) {
            return;
        }

        const listItemMap = createSectionMap(orderList.querySelectorAll('[data-dashboard-order-item]'));
        if (listItemMap.size === 0) {
            return;
        }

        bindDragAndDrop();
        bindMoveButtons();

        const closeButtons = modal.querySelectorAll('[data-dashboard-order-close]');

        const closeModal = () => {
            if (modal.hidden) {
                return;
            }

            modal.hidden = true;
            focusScope.deactivate();
        };
        const focusScope = createModalFocusScope({
            dialog,
            requestClose: closeModal,
        });
        if (focusScope === null) {
            return;
        }

        const openModal = () => {
            modal.hidden = false;
            statusMessage.textContent = 'Drag items or use the arrows to reorder.';
            focusScope.activate(openButton);
            window.requestAnimationFrame(() => focusScope.focus());
        };

        // The last order the server confirmed. A failed write rolls the UI back to it, so the modal
        // never shows an arrangement the account does not actually have.
        let confirmedOrder = currentOrderFromList(orderList);

        const saveOrderFromModal = createReorderWriteQueue(async (isSuperseded) => {
            const nextOrder = currentOrderFromList(orderList);
            applySectionOrder(nextOrder);
            statusMessage.textContent = 'Saving order...';

            const savedOrder = await persistOrder(nextOrder);

            // A change made while this write was in flight is about to be saved from the current DOM.
            // Applying anything here - the stale server answer or a rollback - would undo it, so the
            // follow-up run owns the outcome.
            if (isSuperseded()) {
                confirmedOrder = savedOrder ?? confirmedOrder;
                return;
            }

            if (savedOrder === null) {
                applyOrder(confirmedOrder);
                statusMessage.textContent = 'Order could not be saved, so the previous order was restored.';
                return;
            }

            confirmedOrder = savedOrder;
            applyOrder(savedOrder);
            statusMessage.textContent = 'Order saved.';
        });

        function applyOrder(order) {
            // Re-appending list items moves the focused arrow button in the DOM, which drops focus and
            // breaks repeated keyboard presses. Put it back where the user left it.
            const focusedElement = document.activeElement;
            applyModalOrder(order);
            applySectionOrder(order);
            if (focusedElement instanceof HTMLElement && orderList.contains(focusedElement)) {
                focusedElement.focus();
            }
        }

        function applyModalOrder(order) {
            const fragment = document.createDocumentFragment();

            order.forEach((statusValue) => {
                const item = listItemMap.get(statusValue);
                if (!(item instanceof HTMLElement)) {
                    return;
                }

                fragment.append(item);
            });

            orderList.append(fragment);
        }

        function bindMoveButtons() {
            orderList.addEventListener('click', (event) => {
                const button = event.target instanceof HTMLElement
                    ? event.target.closest('[data-dashboard-order-move]')
                    : null;
                if (!(button instanceof HTMLElement)) {
                    return;
                }

                const item = button.closest('[data-dashboard-order-item]');
                if (!(item instanceof HTMLElement)) {
                    return;
                }

                const isUp = button.dataset.dashboardOrderMove === 'up';
                const sibling = isUp ? item.previousElementSibling : item.nextElementSibling;
                if (!(sibling instanceof HTMLElement)) {
                    return;
                }

                if (isUp) {
                    orderList.insertBefore(item, sibling);
                } else {
                    orderList.insertBefore(sibling, item);
                }

                button.focus();
                void saveOrderFromModal();
            });
        }

        function bindDragAndDrop() {
            let draggedItem = null;

            const clearDropTargets = () => {
                orderList.querySelectorAll('.is-drop-before, .is-drop-after').forEach((item) => {
                    if (!(item instanceof HTMLElement)) {
                        return;
                    }

                    item.classList.remove('is-drop-before', 'is-drop-after');
                });
            };

            orderList.addEventListener('dragstart', (event) => {
                const target = event.target instanceof HTMLElement
                    ? event.target.closest('[data-dashboard-order-item]')
                    : null;

                if (!(target instanceof HTMLElement)) {
                    return;
                }

                draggedItem = target;
                draggedItem.classList.add('is-dragging');
                orderList.classList.add('is-drag-active');
                statusMessage.textContent = 'Release to save the new order.';

                if (event.dataTransfer !== null) {
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('text/plain', draggedItem.dataset.status ?? '');
                }
            });

            orderList.addEventListener('dragover', (event) => {
                if (!(draggedItem instanceof HTMLElement)) {
                    return;
                }

                event.preventDefault();

                const target = event.target instanceof HTMLElement
                    ? event.target.closest('[data-dashboard-order-item]')
                    : null;

                if (!(target instanceof HTMLElement) || target === draggedItem) {
                    clearDropTargets();
                    return;
                }

                const { top, height } = target.getBoundingClientRect();
                const shouldInsertBefore = event.clientY < top + (height / 2);
                clearDropTargets();
                target.classList.add(shouldInsertBefore ? 'is-drop-before' : 'is-drop-after');

                if (shouldInsertBefore) {
                    orderList.insertBefore(draggedItem, target);
                    return;
                }

                orderList.insertBefore(draggedItem, target.nextSibling);
            });

            orderList.addEventListener('drop', (event) => {
                event.preventDefault();
                clearDropTargets();
            });

            orderList.addEventListener('dragend', () => {
                if (!(draggedItem instanceof HTMLElement)) {
                    return;
                }

                draggedItem.classList.remove('is-dragging');
                orderList.classList.remove('is-drag-active');
                clearDropTargets();
                draggedItem = null;
                void saveOrderFromModal();
            });
        }

        openButton.addEventListener('click', openModal);
        closeButtons.forEach((button) => {
            button.addEventListener('click', closeModal);
        });
    }

    return {
        initializeModal,
    };
}

/**
 * @returns {Promise<Array<string>|null>} the order as stored, or null when the write failed
 */
async function persistOrder(order) {
    const route = generateRoute('app_hardware_platforms_status_reorder');
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
    if (typeof route !== 'string' || csrfToken === '') {
        return null;
    }

    const body = new FormData();
    body.append('csrf_token', csrfToken);
    order.forEach((statusValue) => body.append('status_ids[]', statusValue));

    try {
        const response = await fetch(route, {
            method: 'POST',
            body,
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        });

        if (!response.ok) {
            return null;
        }

        const payload = await response.json();

        return payload?.success === true && Array.isArray(payload.order) ? payload.order : null;
    } catch (error) {
        console.error('Dashboard order could not be saved', error);
        return null;
    }
}

function currentOrderFromList(orderList) {
    return Array.from(orderList.querySelectorAll('[data-dashboard-order-item]'))
        .map((item) => (item instanceof HTMLElement ? String(item.dataset.status ?? '').trim() : ''))
        .filter((statusValue) => statusValue !== '');
}
